import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { useContext, useEffect, useCallback, useRef } from 'react';

import {
  Message,
  MessageReportData,
  GeneratedReport,
} from 'components/ChatBV/types';
import { fetchReportByPermalink } from 'components/ChatBV/utils';

interface UseReportLoaderProps {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  generatedReports: Record<string, GeneratedReport>;
  setGeneratedReports: React.Dispatch<
    React.SetStateAction<Record<string, GeneratedReport>>
  >;
}

export const useReportLoader = ({
  messages,
  setMessages,
  generatedReports,
  setGeneratedReports,
}: UseReportLoaderProps) => {
  const { logError } = useContext(AppConfig);
  const loadingRef = useRef<Set<string>>(new Set());

  const loadReportForMessage = useCallback(
    async (message: Message, index: number) => {
      const messageKey = `${message.permalink}-${index}`;

      // Skip if already loading or doesn't meet conditions
      if (
        loadingRef.current.has(messageKey) ||
        !message.permalink ||
        !message.searchType ||
        message.isLoadingReport !== true ||
        message.reports
      ) {
        return;
      }

      // Helper function to safely update message
      const updateMessage = (updates: Partial<Message>) => {
        setMessages((prevMessages: Message[]) => {
          const updatedMessages = [...prevMessages];
          const currentMessage = updatedMessages[index];
          if (
            currentMessage &&
            currentMessage.permalink === message.permalink
          ) {
            updatedMessages[index] = {
              ...currentMessage,
              ...updates,
            };
          }
          return updatedMessages;
        });
      };

      // Check if report already exists in generatedReports
      if (generatedReports[message.permalink]) {
        const existingReport = generatedReports[message.permalink];
        const reportData: MessageReportData = {
          reportData: existingReport.reportData,
          type: existingReport.searchType,
          permalink: existingReport.permalink,
        };
        updateMessage({
          reports: [reportData],
          isLoadingReport: false,
        });
        return;
      }

      // Mark as loading
      loadingRef.current.add(messageKey);

      try {
        const { rawData } = await fetchReportByPermalink(
          message.permalink,
          message.searchType
        );

        if (rawData && message.permalink) {
          const permalink: string = message.permalink;
          // Save to generatedReports to avoid loading again
          setGeneratedReports((prev) => ({
            ...prev,
            [permalink]: {
              reportData: rawData,
              searchType: message.searchType!,
              permalink,
            },
          }));

          // Create MessageReportData from the loaded report
          const reportData: MessageReportData = {
            reportData: rawData,
            type: message.searchType,
            permalink: message.permalink,
          };

          updateMessage({
            reports: [reportData],
            isLoadingReport: false,
          });
        } else {
          updateMessage({ isLoadingReport: false });
        }
      } catch (error) {
        logError('Error loading report for message', error as Error);
        updateMessage({ isLoadingReport: false });
      } finally {
        // Remove from loading set
        loadingRef.current.delete(messageKey);
      }
    },
    [setMessages, setGeneratedReports, generatedReports, logError]
  );

  useEffect(() => {
    // Find all messages that need report loading
    messages.forEach((message, index) => {
      if (
        message.isLoadingReport === true &&
        message.permalink &&
        message.searchType &&
        !message.reports
      ) {
        loadReportForMessage(message, index);
      }
    });
  }, [messages, loadReportForMessage]);
};
